import NativePrintSettingsModal from '@/components/modals/NativePrintSettingsModal.vue';
import V2PrintingModal from '@/components/modals/printing/V2PrintingModal.vue';
import V2NativePrintingModal from '@/components/modals/printing/native/V2NativePrintingModal.vue';
import PrintingUpgradeModal from '@/components/modals/printing/PrintingUpgradeModal.vue';
import { usePrintingStore } from '@/composition/use-printing';
import {
  getSnoozedUntil,
  isSnoozeActive,
} from '@/composition/printing/use-printing-upgrade';
import { useModal } from '@/composition/use-modal';
import { Globals } from '@/modules/Globals';
import { vueData } from '@/VueData';

/** Structural $modal type so legacy class components can pass `this.$modal`. */
interface IModalShowLike {
  show: (
    component: unknown,
    props?: unknown,
    params?: unknown,
    events?: unknown
  ) => void;
}

const MODAL_PARAMS = {
  adaptive: true,
  height: 'auto',
  width: 900,
  scrollable: true,
};

/** Deep-link options; ignored by routes whose modal has no matching tab. */
export interface IPrintingSettingsModalOptions {
  /** Tab to open on ('printers' | 'jobs' | 'preferences' | 'help'). */
  initialTab?: string;
}

function openLegacyPrintSettingsModal(modal: IModalShowLike) {
  modal.show(
    NativePrintSettingsModal,
    vueData,
    { ...MODAL_PARAMS, maxWidth: window.innerWidth * 0.95 },
    {}
  );
}

function openV2PrintingModal(
  modal: IModalShowLike,
  options?: IPrintingSettingsModalOptions
) {
  modal.show(
    V2PrintingModal,
    { initialTab: options?.initialTab },
    { ...MODAL_PARAMS, width: 960, maxWidth: window.innerWidth * 0.95 },
    {}
  );
}

function openNativePrintingModal(
  modal: IModalShowLike,
  options?: IPrintingSettingsModalOptions
) {
  modal.show(
    V2NativePrintingModal,
    { initialTab: options?.initialTab },
    { ...MODAL_PARAMS, width: 960, maxWidth: window.innerWidth * 0.95 },
    {}
  );
}

/**
 * Single dispatch point for the printing settings modal:
 * - Capacitor Android → the new V2NativePrintingModal (native strategy UI),
 *   except legacy shells (old APKs without the EscPosPrinter plugin), which
 *   keep the OLD native settings modal — they print through their bundled
 *   cordova ThermalPrinter plugin until the APK is updated; other Capacitor
 *   platforms (iOS) have no printing and fall through to the V2 modal's
 *   install CTA;
 * - legacy plugin connected → the upgrade interstitial (once per 7-day
 *   snooze window), whose follow-up actions reopen the OLD modal ("Más
 *   tarde") or the new V2 modal ("Actualizar ahora"); while snoozed, the
 *   OLD modal opens directly, exactly as before;
 * - bridge / none / unknown → the new V2PrintingModal (which renders the
 *   install CTA while no bridge is present).
 *
 * Follow-up actions are passed to the interstitial as function props
 * (vue-js-modal forwards the component-props argument as props; its 4th
 * `events` argument binds to the modal wrapper, not the inner component),
 * keeping modal params and routing in this one file with no import cycle.
 *
 * Plain exported function so legacy class components can call it directly
 * with `this.$modal`.
 */
export function openPrintingSettingsModal(
  modal: IModalShowLike,
  options?: IPrintingSettingsModalOptions
) {
  const store = usePrintingStore();

  if (Globals.isCapacitorAndroid) {
    // Old APK: the pre-V2 settings UI drives the cordova fallback strategy,
    // exactly as before this deploy. initialTab is silently ignored here
    // (accepted degradation — legacy modal has no tabs).
    if (Globals.isLegacyAndroidShell) {
      openLegacyPrintSettingsModal(modal);
      return;
    }
    openNativePrintingModal(modal, options);
    return;
  }

  if (store.bridgeMode === 'legacy') {
    if (isSnoozeActive(getSnoozedUntil(), Date.now())) {
      openLegacyPrintSettingsModal(modal);
      return;
    }

    modal.show(
      PrintingUpgradeModal,
      {
        onAccept: () => openV2PrintingModal(modal),
        onLater: () => openLegacyPrintSettingsModal(modal),
      },
      {
        adaptive: true,
        height: 'auto',
        width: 560,
        maxWidth: window.innerWidth * 0.95,
        scrollable: true,
        name: 'printing-upgrade-modal',
        // Backdrop clicks must not bypass the snooze: dismissal goes through
        // the modal's own buttons/X (which snooze and track).
        clickToClose: false,
      },
      {}
    );
    return;
  }

  openV2PrintingModal(modal, options);
}

/** Composition-API flavor for script-setup components. */
export function usePrintingSettingsModal() {
  const modals = useModal();

  return {
    open: (options?: IPrintingSettingsModalOptions) =>
      openPrintingSettingsModal({ show: modals.show }, options),
  };
}
